import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Search, 
  Filter, 
  Calendar,
  TrendingUp,
  Eye,
  Trash2,
  Star,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { getAnalysisHistory, deleteAnalysis } from '../services/api';

const AnalysisHistory = ({ onViewAnalysis }) => {
  const { t } = useTranslation();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  useEffect(() => {
    loadAnalyses();
  }, []);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getAnalysisHistory(50);
      
      if (response.success) {
        setAnalyses(response.analyses || []);
      } else {
        throw new Error(response.message || 'Failed to load analyses');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading analyses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (analysisId) => {
    if (!window.confirm(t('history.confirmDelete') || 'Are you sure you want to delete this analysis?')) {
      return;
    }

    try {
      const response = await deleteAnalysis(analysisId);
      
      if (response.success) {
        setAnalyses(prev => prev.filter(a => a.id !== analysisId));
      } else {
        alert('Failed to delete analysis: ' + response.message);
      }
    } catch (err) {
      alert('Error deleting analysis: ' + err.message);
    }
  };

  const filteredAnalyses = analyses
    .filter(analysis => {
      if (filterStatus !== 'all' && analysis.status !== filterStatus) {
        return false;
      }
      
      if (searchTerm && !analysis.opponent_team.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'recent') {
        return new Date(b.created_at) - new Date(a.created_at);
      } else if (sortBy === 'oldest') {
        return new Date(a.created_at) - new Date(b.created_at);
      } else if (sortBy === 'opponent') {
        return a.opponent_team.localeCompare(b.opponent_team);
      }
      return 0;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('common.error')}</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={loadAnalyses}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
        >
          {t('history.tryAgain') || 'Try Again'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('history.title')}</h1>
          <p className="text-gray-600 mt-1">
            {analyses.length} {t('history.analysesTotal') || 'analyses total'}
          </p>
        </div>
        
        <button
          onClick={loadAnalyses}
          className="flex items-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <TrendingUp className="h-5 w-5" />
          <span>{t('history.refresh') || 'Refresh'}</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-lg p-4">
        <div className="grid md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 rtl:right-3 rtl:left-auto top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('history.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 rtl:pr-10 rtl:pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 rtl:right-3 rtl:left-auto top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full pl-10 rtl:pr-10 rtl:pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent appearance-none"
            >
              <option value="all">{t('history.all')}</option>
              <option value="completed">{t('history.completed')}</option>
              <option value="processing">{t('history.processing')}</option>
              <option value="failed">{t('history.failed')}</option>
            </select>
          </div>

          {/* Sort By */}
          <div className="relative">
            <Calendar className="absolute left-3 rtl:right-3 rtl:left-auto top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full pl-10 rtl:pr-10 rtl:pl-4 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent appearance-none"
            >
              <option value="recent">{t('history.mostRecent') || 'Most Recent'}</option>
              <option value="oldest">{t('history.oldestFirst') || 'Oldest First'}</option>
              <option value="opponent">{t('history.byOpponent') || 'By Opponent'}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Analyses Grid */}
      {filteredAnalyses.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('history.noResults')}</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || filterStatus !== 'all' 
              ? t('history.tryDifferent')
              : t('dashboard.getStarted')}
          </p>
          {(searchTerm || filterStatus !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              {t('history.clearFilters') || 'Clear Filters'}
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAnalyses.map((analysis) => (
            <AnalysisCard
              key={analysis.id}
              analysis={analysis}
              onView={() => onViewAnalysis(analysis.id)}
              onDelete={() => handleDelete(analysis.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Analysis Card Component
const AnalysisCard = ({ analysis, onView, onDelete }) => {
  const { t, i18n } = useTranslation();
  
  const statusConfig = {
    completed: {
      color: 'bg-green-100 text-green-800',
      icon: '✓',
      label: t('analysis.completed')
    },
    processing: {
      color: 'bg-blue-100 text-blue-800',
      icon: '⏳',
      label: t('analysis.processing')
    },
    failed: {
      color: 'bg-red-100 text-red-800',
      icon: '✗',
      label: t('analysis.failed')
    }
  };

  const status = statusConfig[analysis.status] || statusConfig.completed;
  const createdDate = new Date(analysis.created_at);
  const matchDate = analysis.match_date ? new Date(analysis.match_date) : null;

  return (
    <div className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-lg font-bold truncate flex-1">{analysis.opponent_team}</h3>
          <span className={`px-2 py-1 rounded text-xs font-medium ${status.color}`}>
            {status.icon} {status.label}
          </span>
        </div>
        
        {matchDate && (
          <p className="text-blue-100 text-sm">
            {t('upload.matchDate')}: {matchDate.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')}
          </p>
        )}
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="space-y-3 mb-4">
          <div className="flex items-center text-sm text-gray-600">
            <Calendar className="h-4 w-4 mr-2 rtl:ml-2 rtl:mr-0" />
            <span>{t('history.analyzed') || 'Analyzed'}: {createdDate.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US')}</span>
          </div>

          {analysis.confidence_score && (
            <div className="flex items-center text-sm text-gray-600">
              <Star className="h-4 w-4 mr-2 rtl:ml-2 rtl:mr-0 text-yellow-500" />
              <span>{t('history.confidence') || 'Confidence'}: {analysis.confidence_score}%</span>
            </div>
          )}

          {analysis.processing_time && (
            <div className="flex items-center text-sm text-gray-600">
              <TrendingUp className="h-4 w-4 mr-2 rtl:ml-2 rtl:mr-0" />
              <span>{t('history.processedIn') || 'Processed in'} {analysis.processing_time}s</span>
            </div>
          )}
        </div>

        {/* Weaknesses Preview */}
        {analysis.weaknesses && analysis.weaknesses.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-gray-500 mb-2">{t('history.keyWeaknesses') || 'KEY WEAKNESSES'}</p>
            <div className="space-y-1">
              {analysis.weaknesses.slice(0, 2).map((weakness, index) => (
                <div key={index} className="text-sm text-gray-700 flex items-start">
                  <span className="text-red-600 mr-1 rtl:ml-1 rtl:mr-0">•</span>
                  <span className="truncate">{weakness}</span>
                </div>
              ))}
              {analysis.weaknesses.length > 2 && (
                <p className="text-xs text-gray-500">+{analysis.weaknesses.length - 2} {t('history.more') || 'more'}</p>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-2 rtl:space-x-reverse">
          <button
            onClick={onView}
            className="flex-1 flex items-center justify-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Eye className="h-4 w-4" />
            <span>{t('common.view')}</span>
          </button>
          
          <button
            onClick={onDelete}
            className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisHistory;
